import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthGuard } from '~/lib/auth/auth-guard';
import { AppShell } from '~/components/shell/app-shell';
import { ComingSoonPlaceholder } from '~/components/coming-soon-placeholder';
import { menuPaths } from '~/lib/menu';
import { APPOINTMENTS_MODULE, CONTESTS_MODULE, useHasSpecialModule, WISHLIST_TEMPLATES_MODULE } from '~/lib/auth/use-has-special-module';
import { Login } from '~/pages/Login';
import { PromotionsGrid } from '~/pages/Promotions/PromotionsGrid';
import { PromotionEditor } from '~/pages/Promotions/PromotionEditor';
import { ProductsGrid } from '~/pages/Products/ProductsGrid';
import { Languages } from '~/pages/System/Languages';
import { AttributeCategories } from '~/pages/Attributes/Categories';
import { Attributes } from '~/pages/Attributes/List';
import { CategoryAttributesMap } from '~/pages/Attributes/CategoryMap';
import { SizeGuides } from '~/pages/Attributes/SizeGuides';
import { Dimensions } from '~/pages/Attributes/Dimensions';
import { DimensionGroupings } from '~/pages/Attributes/DimensionGroupings';
import { Countries } from '~/pages/System/Countries';
import { Users } from '~/pages/System/Users';
import { Settings as SystemSettings } from '~/pages/System/Settings';
import { CountryRegions } from '~/pages/System/CountryRegions';
import { Cities } from '~/pages/System/Cities';
import { SystemLog } from '~/pages/System/SystemLog';
import { ErpLogs } from '~/pages/System/ErpLogs';
import { ActivityLog } from '~/pages/System/ActivityLog';
import { SitemapPages } from '~/pages/System/SitemapPages';
import { RobotsFile } from '~/pages/System/RobotsFile';
import { UrlRedirects } from '~/pages/System/UrlRedirects';
import { SmsTemplates } from '~/pages/System/SmsTemplates';
import { TaxOffices } from '~/pages/System/TaxOffices';
import { UserRoles } from '~/pages/System/UserRoles';
import { PostalCodes } from '~/pages/System/PostalCodes';
import { EventSubscriptions } from '~/pages/System/EventSubscriptions';
import { Sites } from '~/pages/System/Sites';
import { TaskSchedules } from '~/pages/System/TaskSchedules';
import { ApiKeys } from '~/pages/System/ApiKeys';
import { AiPendingActions } from '~/pages/System/AiPendingActions';
import { McpKeys } from '~/pages/System/McpKeys';
import { EmailTemplates } from '~/pages/System/EmailTemplates';
import { AvailableDates } from '~/pages/System/AvailableDates';
import { PriceTypes } from '~/pages/Prices/PriceTypes';
import { Currencies } from '~/pages/Prices/Currencies';
import { TaxCategories } from '~/pages/Prices/TaxCategories';
import { TaxRules } from '~/pages/Prices/TaxRules';
import { RelationTypes } from '~/pages/Products/RelationTypes';
import { Statuses } from '~/pages/Products/Statuses';
import { Manufacturers } from '~/pages/Products/Manufacturers';
import { BuyGuides } from '~/pages/Products/BuyGuides';
import { Bundles } from '~/pages/Products/Bundles';
import { Reviews } from '~/pages/Products/Reviews';
import { Categories } from '~/pages/Products/Categories';
import { Filters } from '~/pages/Products/Filters';
import { Seasons } from '~/pages/Products/Seasons';
import { CustomerTiers } from '~/pages/Customers/Tiers';
import { CustomerCategories } from '~/pages/Customers/Categories';
import { CustomerGroups } from '~/pages/Customers/Groups';
import { CustomersList } from '~/pages/Customers/List';
import { RegisterRequests } from '~/pages/Customers/RegisterRequests';
import { SalesPersons } from '~/pages/Customers/SalesPersons';
import { Inspectors } from '~/pages/Customers/Inspectors';
import { Models } from '~/pages/Products/Models';
import { Warehouses } from '~/pages/Products/Warehouses';
import { BackInStockSubscriptions } from '~/pages/Products/BackInStockSubscriptions';
import { ThemeTemplates } from '~/pages/Products/ThemeTemplates';
import { WishlistTemplates } from '~/pages/Products/WishlistTemplates';
import { FilterTemplates } from '~/pages/Products/FilterTemplates';
import { OrderStates } from '~/pages/Orders/OrderStates';
import { PaymentTypes } from '~/pages/Orders/PaymentTypes';
import { TakeawayStores } from '~/pages/Orders/TakeawayStores';
import { SkroutzOrders } from '~/pages/Orders/SkroutzOrders';
import { ShippingTypes } from '~/pages/Orders/ShippingTypes';
import { FilteredOrders } from '~/pages/Orders/FilteredOrders';
import { ContributorsList } from '~/pages/Contributors/List';
import { ContributorCategories } from '~/pages/Contributors/Categories';
import { ContributorGroups } from '~/pages/Contributors/Groups';
import { LoyaltyRanges } from '~/pages/Loyalty/Ranges';
import { LoyaltyPerEntity } from '~/pages/Loyalty/PerEntity';
import { LoyaltyOverview } from '~/pages/Loyalty/Overview';
import { LoyaltyProducts } from '~/pages/Loyalty/Products';
import { LoyaltyCustomers } from '~/pages/Loyalty/Customers';
import { TierRules } from '~/pages/Loyalty/TierRules';

// Dashboard is lazy-loaded so Recharts (its trend chart) stays out of the initial bundle.
const Dashboard = lazy(() => import('~/pages/Dashboard').then(m => ({ default: m.Dashboard })));

// Orders grid is lazy-loaded to keep its grid bundle out of the initial chunk (mirrors the
// product editor's split).
const OrdersGrid = lazy(() => import('~/pages/Orders/OrdersGrid').then(m => ({ default: m.OrdersGrid })));

// Order detail editor is lazy-loaded (mirrors the product editor split) so its reused order
// component bundle stays out of the grid/main chunk.
const OrderDetail = lazy(() => import('~/pages/Orders/OrderDetail'));

// Order create surface is lazy-loaded (its reused order component bundle stays out of the
// grid/main chunk). Route `/orders/new` ranks above the `:id` route below.
const OrderCreate = lazy(() => import('~/pages/Orders/OrderCreate'));

// Product editor is lazy-loaded so the tab bodies' heavy deps (TipTap, CodeMirror,
// lightbox) that land in later phases stay code-split out of the grid/main chunk.
const ProductEditor = lazy(() => import('~/pages/Products/ProductEditor'));

// Status editor is lazy-loaded because its Content tab pulls in the TipTap rich-text editor.
const StatusEditor = lazy(() => import('~/pages/Products/Statuses/StatusEditor'));

// Manufacturer editor is lazy-loaded because its Description tab pulls in the TipTap rich-text editor.
const ManufacturerEditor = lazy(() => import('~/pages/Products/Manufacturers/ManufacturerEditor'));

// Buy-guide editor is lazy-loaded because its Content tab pulls in the TipTap rich-text editor.
const BuyGuideEditor = lazy(() => import('~/pages/Products/BuyGuides/BuyGuideEditor'));
const BundleEditor = lazy(() => import('~/pages/Products/Bundles/BundleEditor'));

// Payment-type editor is lazy-loaded because its Content tab pulls in the TipTap rich-text editor.
const PaymentTypeEditor = lazy(() => import('~/pages/Orders/PaymentTypes/PaymentTypeEditor'));

// Pickup-point (takeaway store) editor is lazy-loaded because its Content/Work-hours tabs pull in TipTap.
const TakeawayStoreEditor = lazy(() => import('~/pages/Orders/TakeawayStores/TakeawayStoreEditor'));

// Shipping-type editor is lazy-loaded because its Content tab pulls in TipTap and it bundles the
// charges/payments/postal-code relation sub-grids.
const ShippingTypeEditor = lazy(() => import('~/pages/Orders/ShippingTypes/ShippingTypeEditor'));

// Bookings module pages (BW-8351) are lazy-loaded: the module is flag-gated and off by default.
const BookingsList = lazy(() => import('~/pages/Bookings/List'));
const BookingRepresentatives = lazy(() => import('~/pages/Bookings/Representatives'));
const BookingTypes = lazy(() => import('~/pages/Bookings/Types'));
const BookingTypeEditor = lazy(() => import('~/pages/Bookings/Types/BookingTypeEditor'));
const BookingReports = lazy(() => import('~/pages/Bookings/Reports'));

// Category editor is lazy-loaded because its Descriptions tab pulls in the TipTap rich-text editor.
const CategoryEditor = lazy(() => import('~/pages/Products/Categories/CategoryEditor'));

// Size-guide editor is lazy-loaded because its body pulls in the TipTap rich-text editor.
const SizeGuideEditor = lazy(() => import('~/pages/Attributes/SizeGuides/SizeGuideEditor'));

// Content pages grid + editor. The editor is lazy-loaded because its body tab pulls in the
// TipTap/CodeMirror rich-text editor, which must stay out of the eager/main chunk.
// Coupons editor pulls in the product/customer/category pickers + tabs, so keep the page lazy.
const Coupons = lazy(() => import('~/pages/Promotions/Coupons').then(m => ({ default: m.Coupons })));
const DiscountCaps = lazy(() => import('~/pages/Promotions/DiscountCaps').then(m => ({ default: m.DiscountCaps })));

// Contests editor pulls in the HTML source editor (CodeMirror) + product search, so keep it lazy.
const ContestsPage = lazy(() => import('~/pages/Modules/Contests').then(m => ({ default: m.Contests })));

// Product Tabs editor pulls in the TipTap rich-text editor + product search, so keep it lazy.
const ProductTabsPage = lazy(() => import('~/pages/Modules/ProductTabs').then(m => ({ default: m.ProductTabs })));

const ContentPages = lazy(() => import('~/pages/Content/ContentPages'));
const ContentPageEditor = lazy(() => import('~/pages/Content/ContentPageEditor'));

// Banner groups (dialog-based grid; lazy for parity with the other content grids).
const BannerGroups = lazy(() => import('~/pages/Content/BannerGroups'));

// Banners grid + editor; the editor is lazy because its Content tab pulls in TipTap/CodeMirror.
const Banners = lazy(() => import('~/pages/Content/Banners'));
const BannerEditor = lazy(() => import('~/pages/Content/BannerEditor'));

// Menus tree + editor; the editor is lazy because its Content tab pulls in TipTap/CodeMirror.
const Menus = lazy(() => import('~/pages/Content/Menus'));
const MenuItemEditor = lazy(() => import('~/pages/Content/MenuItemEditor'));
// Footer tree + editor (same shape as Menus: columns with their links)
const Footer = lazy(() => import('~/pages/Content/Footer'));
const FooterItemEditor = lazy(() => import('~/pages/Content/FooterItemEditor'));

// FAQs grid + editor; the editor's entry sheet pulls in TipTap, so it stays lazy.
const Faqs = lazy(() => import('~/pages/Content/Faqs'));
const FaqEditor = lazy(() => import('~/pages/Content/FaqEditor'));

// News grid + editor; the editor's body tabs pull in TipTap, so it stays lazy.
const News = lazy(() => import('~/pages/Content/News'));
const NewsEditor = lazy(() => import('~/pages/Content/NewsEditor'));
// Events grid + editor; the editor's body tabs pull in TipTap, so it stays lazy.
const Events = lazy(() => import('~/pages/Content/Events'));
const EventsEditor = lazy(() => import('~/pages/Content/EventsEditor'));

// Social media platforms (dialog-based grid; lazy for parity with the other content grids).
const SocialMedia = lazy(() => import('~/pages/Content/SocialMedia'));

// Logos (dialog-based grid with two image slots; lazy for parity with the other content grids).
const Logo = lazy(() => import('~/pages/Content/Logo'));

// Custom-message groups (dialog-based lookup grid).
const CustomMessageGroups = lazy(() => import('~/pages/Content/CustomMessageGroups'));

// Simple content lookup grids (dialog-based; name/code/visibility/sort).
const NewsGroups = lazy(() => import('~/pages/Content/NewsGroups'));
const VideoGroups = lazy(() => import('~/pages/Content/VideoGroups'));
const NotificationCategories = lazy(() => import('~/pages/Content/NotificationCategories'));
const NewsEntriesTags = lazy(() => import('~/pages/Content/NewsEntriesTags'));
const EventTags = lazy(() => import('~/pages/Content/EventTags'));
const NewsCategories = lazy(() => import('~/pages/Content/NewsCategories'));
const EventCategories = lazy(() => import('~/pages/Content/EventCategories'));
const VideoCategories = lazy(() => import('~/pages/Content/VideoCategories'));
// Custom messages editor dialog pulls in TipTap, so keep it lazy.
const CustomMessages = lazy(() => import('~/pages/Content/CustomMessages'));
const EventLocations = lazy(() => import('~/pages/Content/EventLocations'));
const ContactForms = lazy(() => import('~/pages/Content/ContactForms'));
const PushSubscriptions = lazy(() => import('~/pages/Content/PushSubscriptions'));
// Videos editor dialog pulls in TipTap, so keep it lazy.
const Videos = lazy(() => import('~/pages/Content/Videos'));
// Notifications editor dialog pulls in TipTap, so keep it lazy.
const Notifications = lazy(() => import('~/pages/Content/Notifications'));
const FileManager = lazy(() => import('~/pages/Content/FileManager'));
const CustomContent = lazy(() => import('~/pages/Content/CustomContent'));
const CustomContentEditor = lazy(() => import('~/pages/Content/CustomContentEditor'));

// System full-page editors (mirror the content editors' split) are lazy-loaded so their form
// bundles stay out of the System grid/main chunk. `:id` also matches the literal `create`
// (except event-subscriptions, which is edit-only — those records are created by the storefront).
const EmailTemplateEditor = lazy(() => import('~/pages/System/EmailTemplates/EmailTemplateEditor'));
const SmsTemplateEditor = lazy(() => import('~/pages/System/SmsTemplates/SmsTemplateEditor'));
const SiteEditor = lazy(() => import('~/pages/System/Sites/SiteEditor'));
const UserEditor = lazy(() => import('~/pages/System/Users/UserEditor'));
const TaskScheduleEditor = lazy(() => import('~/pages/System/TaskSchedules/TaskScheduleEditor'));
const EventSubscriptionEditor = lazy(() => import('~/pages/System/EventSubscriptions/EventSubscriptionEditor'));

// Reports pages are lazy-loaded so their Recharts/map bundles stay out of the initial chunk
// (mirrors the Dashboard split — they share the same charting deps).
const SalesReport = lazy(() => import('~/pages/Reports/Sales').then(m => ({ default: m.SalesReport })));
const BrandsReport = lazy(() => import('~/pages/Reports/Brands').then(m => ({ default: m.BrandsReport })));
const CategoriesReport = lazy(() => import('~/pages/Reports/Categories').then(m => ({ default: m.CategoriesReport })));
const ProductsReport = lazy(() => import('~/pages/Reports/Products').then(m => ({ default: m.ProductsReport })));
const CouponsReport = lazy(() => import('~/pages/Reports/Coupons').then(m => ({ default: m.CouponsReport })));
const GeographicReport = lazy(() => import('~/pages/Reports/Geographic').then(m => ({ default: m.GeographicReport })));

// Import/Export (legacy `/import-export` split into two action pages). Lazy-loaded — the export
// page pulls in the product/order filter pickers + lookup hooks, kept out of the main chunk.
const ImportExportImport = lazy(() => import('~/pages/ImportExport/Import').then(m => ({ default: m.ImportExportImport })));
const ImportExportExport = lazy(() => import('~/pages/ImportExport/Export').then(m => ({ default: m.ImportExportExport })));

// Newsletter subscriptions (simple CRUD grid — edit verified flag + delete). Lazy for parity with
// the other marketing-section grids.
const NewsletterSubscriptions = lazy(() => import('~/pages/Newsletters/Subscriptions').then(m => ({ default: m.NewsletterSubscriptions })));

// Gift-card orders (read-only grid + details dialog). Lazy — the details dialog pulls its own
// notification/history queries that stay out of the main chunk.
const GiftCardOrders = lazy(() => import('~/pages/GiftCards/Orders').then(m => ({ default: m.GiftCardOrders })));

// Returns & replacements queue. Lazy — its edit popup pulls the product/variant pickers and the
// per-piece editors, which stay out of the orders grid/main chunk.
const Returns = lazy(() => import('~/pages/Returns').then(m => ({ default: m.Returns })));

// Returns Settings — the module's configurable variables. Lazy for parity with the queue page.
const ReturnSettings = lazy(() => import('~/pages/Returns/Settings').then(m => ({ default: m.ReturnSettings })));

// The /dev/* scratch routes are lazy-loaded: they pull in heavy, dev-only deps (TipTap,
// CodeMirror, the full product/promotion component library) that must never reach the
// production entry chunk. They are not linked from production navigation.
const DevComponents = lazy(() => import('~/pages/Dev/Components').then(m => ({ default: m.DevComponents })));
const DevProductsComponents = lazy(() => import('~/pages/Dev/ProductsComponents').then(m => ({ default: m.DevProductsComponents })));
const DevOrdersComponents = lazy(() => import('~/pages/Dev/OrdersComponents').then(m => ({ default: m.DevOrdersComponents })));
const DevEditorSections = lazy(() => import('~/pages/Dev/EditorSections').then(m => ({ default: m.DevEditorSections })));
const DevEditorActions = lazy(() => import('~/pages/Dev/EditorActions').then(m => ({ default: m.DevEditorActions })));

// Routes that have a real surface in admin-v2 today (or an explicit route registered above);
// everything else in the menu renders the <ComingSoonPlaceholder/> until that page is migrated.
// `/orders/new` is explicitly routed above, so it must be excluded from the ComingSoon map.
const SHIPPED_PATHS = new Set([
	'/promotions',
	'/promotions/coupons',
	'/promotions/discount-caps',
	'/dashboard',
	'/products',
	'/products/relations',
	'/products/statuses',
	'/products/manufacturers',
	'/products/reviews',
	'/products/back-in-stock',
	'/products/categories',
	'/products/filters',
	'/products/seasons',
	'/products/models',
	'/products/warehouses',
	'/products/theme-templates',
	'/products/wishlist-templates',
	'/products/filter-templates',
	'/products/buy-guides',
	'/products/bundles',
	'/attributes',
	'/attributes/categories',
	'/attributes/category-map',
	'/attributes/dimensions',
	'/attributes/dimensions-grouping',
	'/attributes/size-guides',
	'/customers',
	'/customers/tiers',
	'/customers/categories',
	'/customers/groups',
	'/customers/register-requests',
	'/customers/sales-persons',
	'/customers/inspectors',
	'/orders',
	'/orders/new',
	'/orders/internal-statuses',
	'/orders/filtered',
	'/orders/payment-methods',
	'/orders/pickup-points',
	'/orders/shipping-methods',
	'/orders/skroutz',
	'/returns',
	'/returns/settings',
	'/system/languages',
	'/system/countries',
	'/system/users',
	'/system/settings',
	'/system/country-regions',
	'/system/cities',
	'/system/system-log',
	'/system/erp-logs',
	'/system/activity-log',
	'/system/sitemap-pages',
	'/system/robots-file',
	'/system/url-redirects',
	'/system/sms-templates',
	'/system/tax-offices',
	'/system/user-roles',
	'/system/postal-codes',
	'/system/event-subscriptions',
	'/system/sites',
	'/system/task-schedules',
	'/system/api-keys',
	'/system/mcp-keys',
	'/system/ai-pending-actions',
	'/system/email-templates',
	'/system/available-dates',
	'/content/pages',
	'/content/banner-groups',
	'/content/banners',
	'/content/menus',
	'/content/footer',
	'/content/faqs',
	'/content/news',
	'/content/events',
	'/content/social-media',
	'/content/logo',
	'/content/custom-message-groups',
	'/content/news-groups',
	'/content/video-groups',
	'/content/notification-categories',
	'/content/news-entries-tags',
	'/content/event-tags',
	'/content/news-categories',
	'/content/event-categories',
	'/content/video-categories',
	'/content/custom-messages',
	'/content/event-locations',
	'/content/contact-forms',
	'/content/push-subscriptions',
	'/content/videos',
	'/content/notifications',
	'/content/file-manager',
	'/content/custom-content',
	'/prices/types',
	'/prices/currency-rates',
	'/prices/tax-categories',
	'/prices/tax-rules',
	'/contributors',
	'/contributors/categories',
	'/contributors/groups',
	'/loyalty/ranges',
	'/loyalty/per-entity',
	'/loyalty',
	'/loyalty/products',
	'/loyalty/customers',
	'/loyalty/tier-rules',
	'/reports/sales',
	'/reports/brands',
	'/reports/categories',
	'/reports/products',
	'/reports/coupons',
	'/reports/geographic',
	'/modules/contests',
	'/modules/product-tabs',
	'/import-export/import',
	'/import-export/export',
	'/newsletters/subscriptions',
	'/gift-cards/orders',
	'/bookings',
	'/bookings/representatives',
	'/bookings/types',
	'/bookings/reports'
]);

/**
 * Router. Authenticated pages render inside the <AppShell> layout (guarded once at
 * the layout level); `/login` and the `/dev/*` scratch routes stay outside the shell.
 */
export function App() {
	// Optional modules (BW-8453): their routes exist only while the API lists them in BizWebSettings:SpecialModules.
	const appointments = useHasSpecialModule(APPOINTMENTS_MODULE);
	const contests = useHasSpecialModule(CONTESTS_MODULE);
	const wishlistTemplates = useHasSpecialModule(WISHLIST_TEMPLATES_MODULE);

	return (
		<>
			<Routes>
				<Route path="/login" element={<Login />} />

				<Route
					element={
						<AuthGuard>
							<AppShell />
						</AuthGuard>
					}>
					<Route index element={<Navigate to="/dashboard" replace />} />
					<Route
						path="/dashboard"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Dashboard />
							</Suspense>
						}
					/>
					<Route path="/promotions" element={<PromotionsGrid />} />
					<Route
						path="/promotions/coupons"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Coupons />
							</Suspense>
						}
					/>
					<Route
						path="/promotions/discount-caps"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<DiscountCaps />
							</Suspense>
						}
					/>
					<Route path="/promotions/:id" element={<PromotionEditor />} />
					<Route path="/products" element={<ProductsGrid />} />
					<Route path="/products/relations" element={<RelationTypes />} />
					<Route path="/products/statuses" element={<Statuses />} />
					<Route
						path="/products/statuses/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<StatusEditor />
							</Suspense>
						}
					/>
					<Route path="/products/manufacturers" element={<Manufacturers />} />
					<Route path="/products/reviews" element={<Reviews />} />
					<Route path="/products/back-in-stock" element={<BackInStockSubscriptions />} />
					<Route path="/products/categories" element={<Categories />} />
					<Route path="/products/filters" element={<Filters />} />
					<Route path="/products/seasons" element={<Seasons />} />
					<Route path="/products/models" element={<Models />} />
					<Route path="/products/warehouses" element={<Warehouses />} />
					<Route path="/products/theme-templates" element={<ThemeTemplates />} />
					{/* Registered either way: unregistered, the URL would fall through to `/products/:id` as a product id (BW-8477). */}
					<Route
						path="/products/wishlist-templates"
						element={wishlistTemplates ? <WishlistTemplates /> : <Navigate to="/dashboard" replace />}
					/>
					<Route path="/products/filter-templates" element={<FilterTemplates />} />
					<Route path="/products/buy-guides" element={<BuyGuides />} />
					<Route path="/products/bundles" element={<Bundles />} />
					<Route
						path="/products/bundles/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<BundleEditor />
							</Suspense>
						}
					/>
					<Route
						path="/products/buy-guides/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<BuyGuideEditor />
							</Suspense>
						}
					/>
					<Route
						path="/products/manufacturers/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ManufacturerEditor />
							</Suspense>
						}
					/>
					<Route
						path="/products/categories/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<CategoryEditor />
							</Suspense>
						}
					/>
					<Route path="/attributes" element={<Attributes />} />
					<Route path="/attributes/categories" element={<AttributeCategories />} />
					<Route path="/attributes/category-map" element={<CategoryAttributesMap />} />
					<Route path="/attributes/dimensions" element={<Dimensions />} />
					<Route path="/attributes/dimensions-grouping" element={<DimensionGroupings />} />
					<Route path="/attributes/size-guides" element={<SizeGuides />} />
					<Route path="/customers" element={<CustomersList />} />
					<Route path="/customers/tiers" element={<CustomerTiers />} />
					<Route path="/customers/categories" element={<CustomerCategories />} />
					<Route path="/customers/groups" element={<CustomerGroups />} />
					<Route path="/customers/register-requests" element={<RegisterRequests />} />
					<Route path="/customers/sales-persons" element={<SalesPersons />} />
					<Route path="/customers/inspectors" element={<Inspectors />} />
					<Route
						path="/attributes/size-guides/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<SizeGuideEditor />
							</Suspense>
						}
					/>
					<Route path="/system/languages" element={<Languages />} />
					<Route path="/system/countries" element={<Countries />} />
					<Route path="/system/users" element={<Users />} />
					<Route path="/system/settings" element={<SystemSettings />} />
					<Route path="/system/country-regions" element={<CountryRegions />} />
					<Route path="/system/cities" element={<Cities />} />
					<Route path="/system/system-log" element={<SystemLog />} />
					<Route path="/system/erp-logs" element={<ErpLogs />} />
					<Route path="/system/activity-log" element={<ActivityLog />} />
					<Route path="/system/sitemap-pages" element={<SitemapPages />} />
					<Route path="/system/robots-file" element={<RobotsFile />} />
					<Route path="/system/url-redirects" element={<UrlRedirects />} />
					<Route path="/system/sms-templates" element={<SmsTemplates />} />
					<Route path="/system/tax-offices" element={<TaxOffices />} />
					<Route path="/system/user-roles" element={<UserRoles />} />
					<Route path="/system/postal-codes" element={<PostalCodes />} />
					<Route path="/system/event-subscriptions" element={<EventSubscriptions />} />
					<Route path="/system/sites" element={<Sites />} />
					<Route path="/system/task-schedules" element={<TaskSchedules />} />
					<Route path="/system/api-keys" element={<ApiKeys />} />
					<Route path="/system/ai-pending-actions" element={<AiPendingActions />} />
					<Route path="/system/mcp-keys" element={<McpKeys />} />
					<Route path="/system/email-templates" element={<EmailTemplates />} />
					<Route
						path="/system/email-templates/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<EmailTemplateEditor />
							</Suspense>
						}
					/>
					<Route
						path="/system/sms-templates/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<SmsTemplateEditor />
							</Suspense>
						}
					/>
					<Route
						path="/system/sites/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<SiteEditor />
							</Suspense>
						}
					/>
					<Route
						path="/system/users/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<UserEditor />
							</Suspense>
						}
					/>
					<Route
						path="/system/task-schedules/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<TaskScheduleEditor />
							</Suspense>
						}
					/>
					<Route
						path="/system/event-subscriptions/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<EventSubscriptionEditor />
							</Suspense>
						}
					/>
					<Route path="/system/available-dates" element={<AvailableDates />} />
					<Route path="/prices/types" element={<PriceTypes />} />
					<Route path="/prices/currency-rates" element={<Currencies />} />
					<Route path="/prices/tax-categories" element={<TaxCategories />} />
					<Route path="/prices/tax-rules" element={<TaxRules />} />
					<Route path="/contributors" element={<ContributorsList />} />
					<Route path="/contributors/categories" element={<ContributorCategories />} />
					<Route path="/contributors/groups" element={<ContributorGroups />} />
					<Route path="/loyalty/ranges" element={<LoyaltyRanges />} />
					<Route path="/loyalty" element={<LoyaltyOverview />} />
					<Route path="/loyalty/per-entity" element={<LoyaltyPerEntity />} />
					<Route path="/loyalty/products" element={<LoyaltyProducts />} />
					<Route path="/loyalty/customers" element={<LoyaltyCustomers />} />
					<Route path="/loyalty/tier-rules" element={<TierRules />} />
					{contests && (
						<Route
							path="/modules/contests"
							element={
								<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
									<ContestsPage />
								</Suspense>
							}
						/>
					)}
					<Route
						path="/modules/product-tabs"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ProductTabsPage />
							</Suspense>
						}
					/>
					<Route
						path="/reports/sales"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<SalesReport />
							</Suspense>
						}
					/>
					<Route
						path="/reports/brands"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<BrandsReport />
							</Suspense>
						}
					/>
					<Route
						path="/reports/categories"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<CategoriesReport />
							</Suspense>
						}
					/>
					<Route
						path="/reports/products"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ProductsReport />
							</Suspense>
						}
					/>
					<Route
						path="/reports/coupons"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<CouponsReport />
							</Suspense>
						}
					/>
					<Route
						path="/reports/geographic"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<GeographicReport />
							</Suspense>
						}
					/>
					<Route
						path="/orders"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<OrdersGrid />
							</Suspense>
						}
					/>
					{/* The literal `/orders/<child>` menu routes (skroutz/…) are registered via menuPaths
					    below; React Router ranks those static segments above `:id`. */}
					<Route
						path="/orders/new"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<OrderCreate />
							</Suspense>
						}
					/>
					{/* Returns moved out of Orders into its own section; the old path stays as a redirect so
					    existing bookmarks and links do not 404. */}
					<Route path="/orders/returns" element={<Navigate to="/returns" replace />} />
					<Route
						path="/returns"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Returns />
							</Suspense>
						}
					/>
					<Route
						path="/returns/settings"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ReturnSettings />
							</Suspense>
						}
					/>
					<Route path="/orders/skroutz" element={<SkroutzOrders />} />
					<Route path="/orders/filtered" element={<FilteredOrders />} />
					<Route path="/orders/internal-statuses" element={<OrderStates />} />
					<Route path="/orders/payment-methods" element={<PaymentTypes />} />
					<Route
						path="/orders/payment-methods/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<PaymentTypeEditor />
							</Suspense>
						}
					/>
					<Route path="/orders/pickup-points" element={<TakeawayStores />} />
					<Route
						path="/orders/pickup-points/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<TakeawayStoreEditor />
							</Suspense>
						}
					/>
					<Route path="/orders/shipping-methods" element={<ShippingTypes />} />
					<Route
						path="/orders/shipping-methods/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ShippingTypeEditor />
							</Suspense>
						}
					/>
					<Route
						path="/orders/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<OrderDetail />
							</Suspense>
						}
					/>
					<Route
						path="/content/banner-groups"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<BannerGroups />
							</Suspense>
						}
					/>
					<Route
						path="/content/banners"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Banners />
							</Suspense>
						}
					/>
					<Route
						path="/content/banners/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<BannerEditor />
							</Suspense>
						}
					/>
					<Route
						path="/content/menus"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Menus />
							</Suspense>
						}
					/>
					<Route
						path="/content/menus/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<MenuItemEditor />
							</Suspense>
						}
					/>
					<Route
						path="/content/footer"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Footer />
							</Suspense>
						}
					/>
					<Route
						path="/content/footer/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<FooterItemEditor />
							</Suspense>
						}
					/>
					<Route
						path="/content/faqs"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Faqs />
							</Suspense>
						}
					/>
					<Route
						path="/content/faqs/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<FaqEditor />
							</Suspense>
						}
					/>
					<Route
						path="/content/news"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<News />
							</Suspense>
						}
					/>
					<Route
						path="/content/news/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<NewsEditor />
							</Suspense>
						}
					/>
					{appointments && (
						<Route
							path="/content/events"
							element={
								<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
									<Events />
								</Suspense>
							}
						/>
					)}
					{/* `:id` also matches the literal `create`. */}
					{appointments && (
						<Route
							path="/content/events/:id"
							element={
								<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
									<EventsEditor />
								</Suspense>
							}
						/>
					)}
					<Route
						path="/content/social-media"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<SocialMedia />
							</Suspense>
						}
					/>
					<Route
						path="/content/logo"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Logo />
							</Suspense>
						}
					/>
					<Route
						path="/content/custom-message-groups"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<CustomMessageGroups />
							</Suspense>
						}
					/>
					<Route
						path="/content/news-groups"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<NewsGroups />
							</Suspense>
						}
					/>
					<Route
						path="/content/video-groups"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<VideoGroups />
							</Suspense>
						}
					/>
					<Route
						path="/content/notification-categories"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<NotificationCategories />
							</Suspense>
						}
					/>
					<Route
						path="/content/news-entries-tags"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<NewsEntriesTags />
							</Suspense>
						}
					/>
					{appointments && (
						<Route
							path="/content/event-tags"
							element={
								<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
									<EventTags />
								</Suspense>
							}
						/>
					)}
					<Route
						path="/content/news-categories"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<NewsCategories />
							</Suspense>
						}
					/>
					{appointments && (
						<Route
							path="/content/event-categories"
							element={
								<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
									<EventCategories />
								</Suspense>
							}
						/>
					)}
					<Route
						path="/content/video-categories"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<VideoCategories />
							</Suspense>
						}
					/>
					<Route
						path="/content/custom-messages"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<CustomMessages />
							</Suspense>
						}
					/>
					{appointments && (
						<Route
							path="/content/event-locations"
							element={
								<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
									<EventLocations />
								</Suspense>
							}
						/>
					)}
					<Route
						path="/content/contact-forms"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ContactForms />
							</Suspense>
						}
					/>
					<Route
						path="/content/push-subscriptions"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<PushSubscriptions />
							</Suspense>
						}
					/>
					<Route
						path="/content/videos"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Videos />
							</Suspense>
						}
					/>
					<Route
						path="/content/notifications"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<Notifications />
							</Suspense>
						}
					/>
					<Route
						path="/content/file-manager"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<FileManager />
							</Suspense>
						}
					/>
					<Route
						path="/content/custom-content"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<CustomContent />
							</Suspense>
						}
					/>
					{/* `:id` also matches the literal `create` id (useCustomContent handles non-numeric ids). */}
					<Route
						path="/content/custom-content/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<CustomContentEditor />
							</Suspense>
						}
					/>
					<Route
						path="/content/pages"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ContentPages />
							</Suspense>
						}
					/>
					{/* `:id` also matches the literal `create` id (useContentPage handles non-numeric ids). */}
					<Route
						path="/content/pages/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ContentPageEditor />
							</Suspense>
						}
					/>
					{/* `:id` also matches the literal `create` and `demo` ids (useProduct handles both). */}
					<Route
						path="/products/:id"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ProductEditor />
							</Suspense>
						}
					/>
					<Route
						path="/import-export/import"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ImportExportImport />
							</Suspense>
						}
					/>
					<Route
						path="/import-export/export"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<ImportExportExport />
							</Suspense>
						}
					/>
					<Route
						path="/newsletters/subscriptions"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<NewsletterSubscriptions />
							</Suspense>
						}
					/>
					<Route
						path="/gift-cards/orders"
						element={
							<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
								<GiftCardOrders />
							</Suspense>
						}
					/>
					{appointments && (
						<>
							<Route
								path="/bookings"
								element={
									<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
										<BookingsList />
									</Suspense>
								}
							/>
							<Route
								path="/bookings/representatives"
								element={
									<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
										<BookingRepresentatives />
									</Suspense>
								}
							/>
							<Route
								path="/bookings/types"
								element={
									<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
										<BookingTypes />
									</Suspense>
								}
							/>
							<Route
								path="/bookings/types/:id"
								element={
									<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
										<BookingTypeEditor />
									</Suspense>
								}
							/>
							<Route
								path="/bookings/reports"
								element={
									<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
										<BookingReports />
									</Suspense>
								}
							/>
						</>
					)}
					{menuPaths
						.filter(p => !SHIPPED_PATHS.has(p.path))
						.map(p => (
							<Route key={p.path} path={p.path} element={<ComingSoonPlaceholder titleKey={p.labelKey} />} />
						))}
					{/* User-menu destinations (not in the sidebar menu) */}
					<Route path="/profile" element={<ComingSoonPlaceholder titleKey="shell.user.profile" />} />
					<Route path="/settings" element={<ComingSoonPlaceholder titleKey="shell.user.settings" />} />
					<Route path="/activity" element={<ComingSoonPlaceholder titleKey="shell.user.activity" />} />
					{/* Unknown URLs, and the pages of an optional module that is off (BW-8453), go to the dashboard, as in the legacy admin. */}
					<Route path="*" element={<Navigate to="/dashboard" replace />} />
				</Route>

				<Route
					path="/dev/*"
					element={
						<Suspense fallback={<div className="p-6 text-sm text-foreground-subtle">…</div>}>
							<Routes>
								<Route path="components" element={<DevComponents />} />
								<Route path="products-components" element={<DevProductsComponents />} />
								<Route path="orders-components" element={<DevOrdersComponents />} />
								<Route path="editor-sections" element={<DevEditorSections />} />
								<Route path="editor-actions" element={<DevEditorActions />} />
							</Routes>
						</Suspense>
					}
				/>
				{/* Future page migrations replace the matching placeholder route above */}
			</Routes>
			<Toaster position="top-right" richColors closeButton />
		</>
	);
}
